import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

// Redux
import { 
  fetchApiCacheData, 
  clearApiData, 
  selectApiData, 
  selectApiDataStatus, 
  selectApiDataError,
  selectCacheId 
} from '_redux/slices/api-data/api-data';

// Components
import { CustomSpinner } from '_components/atoms';

// Styles
import { Spacing } from '_styles';
import { PRM_BLUE, GRAY_LIGHT, GRAY_DARK, TEXT_COLOR } from '_styles/colors';

// Bootstrap
import { Card, Alert, Button, Container, Row, Col, Form } from 'react-bootstrap';

// Tremor
import { Title, Subtitle } from "@tremor/react";

const ApiDataScreen = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  
  // Local state for search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [highlightedText, setHighlightedText] = useState('');
  const jsonContainerRef = useRef(null);
  
  // Redux state
  const cacheData = useSelector(selectApiData);
  const status = useSelector(selectApiDataStatus);
  const error = useSelector(selectApiDataError);
  const cacheId = useSelector(selectCacheId);

  // Get cache ID from URL query params
  const urlParams = new URLSearchParams(location.search);
  const urlCacheId = urlParams.get('cacheId');

  useEffect(() => {
    if (urlCacheId && urlCacheId !== cacheId) {
      dispatch(clearApiData());
      dispatch(fetchApiCacheData(urlCacheId));
    }
  }, [urlCacheId, cacheId, dispatch]);

  // Search functionality
  const performSearch = (term) => {
    if (!term.trim() || !cacheData) {
      setSearchResults([]);
      setCurrentResultIndex(0);
      setHighlightedText('');
      return;
    }

    const jsonString = JSON.stringify(cacheData, null, 2);
    const regex = new RegExp(term, 'gi');
    const matches = [];
    let match;

    while ((match = regex.exec(jsonString)) !== null) {
      matches.push({
        index: match.index,
        text: match[0]
      });
    }

    setSearchResults(matches);
    setCurrentResultIndex(0);
    setHighlightedText(term);
  };

  const scrollToNextResult = () => {
    if (searchResults.length === 0) return;
    
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    scrollToResult(nextIndex);
  };

  const scrollToPreviousResult = () => {
    if (searchResults.length === 0) return;
    
    const prevIndex = currentResultIndex === 0 ? searchResults.length - 1 : currentResultIndex - 1;
    setCurrentResultIndex(prevIndex);
    scrollToResult(prevIndex);
  };

  const scrollToResult = (index) => {
    if (!jsonContainerRef.current || searchResults.length === 0) return;
    
    const container = jsonContainerRef.current;
    const jsonString = JSON.stringify(cacheData, null, 2);
    const match = searchResults[index];
    
    // Calculate approximate line number
    const textBeforeMatch = jsonString.substring(0, match.index);
    const lineNumber = (textBeforeMatch.match(/\n/g) || []).length;
    
    // Scroll to approximate position
    const lineHeight = 20; // Approximate line height
    const scrollTop = lineNumber * lineHeight;
    
    container.scrollTo({
      top: Math.max(0, scrollTop - 100), // Offset to show context
      behavior: 'smooth'
    });
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    performSearch(term);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setCurrentResultIndex(0);
    setHighlightedText('');
  };

  const handleRefresh = () => {
    if (urlCacheId) {
      dispatch(fetchApiCacheData(urlCacheId));
    }
  };

  const handleClear = () => {
    dispatch(clearApiData());
  };

  const highlightText = (text) => {
    if (!highlightedText || !text) return text;
    
    const regex = new RegExp(`(${highlightedText})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <span 
            key={index} 
            style={{ 
              backgroundColor: '#ffeb3b', 
              color: '#000',
              fontWeight: 'bold',
              padding: '1px 2px',
              borderRadius: '2px'
            }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const renderCacheData = (data, level = 0) => {
    if (data === null || data === undefined) {
      return <span style={{ color: GRAY_DARK, fontStyle: 'italic' }}>null</span>;
    }

    if (typeof data === 'string') {
      return <span style={{ color: '#28a745' }}>"{highlightText(data)}"</span>;
    }

    if (typeof data === 'number') {
      return <span style={{ color: '#007bff' }}>{highlightText(data.toString())}</span>;
    }

    if (typeof data === 'boolean') {
      return <span style={{ color: '#fd7e14' }}>{highlightText(data.toString())}</span>;
    }

    if (Array.isArray(data)) {
      return (
        <div style={{ marginLeft: level * 20 }}>
          <span style={{ color: GRAY_DARK }}>[</span>
          {data.map((item, index) => (
            <div key={index} style={{ marginLeft: 20 }}>
              <span style={{ color: GRAY_DARK }}>{highlightText(index.toString())}:</span> {renderCacheData(item, level + 1)}
              {index < data.length - 1 && <span style={{ color: GRAY_DARK }}>,</span>}
            </div>
          ))}
          <span style={{ color: GRAY_DARK }}>]</span>
        </div>
      );
    }

    if (typeof data === 'object') {
      return (
        <div style={{ marginLeft: level * 20 }}>
          <span style={{ color: GRAY_DARK }}>{'{'}</span>
          {Object.entries(data).map(([key, value], index, array) => (
            <div key={key} style={{ marginLeft: 20 }}>
              <span style={{ color: '#6f42c1', fontWeight: '600' }}>"{highlightText(key)}"</span>
              <span style={{ color: GRAY_DARK }}>: </span>
              {renderCacheData(value, level + 1)}
              {index < array.length - 1 && <span style={{ color: GRAY_DARK }}>,</span>}
            </div>
          ))}
          <span style={{ color: GRAY_DARK }}>{'}'}</span>
        </div>
      );
    }

    return <span>{highlightText(String(data))}</span>;
  };

  const containerStyle = {
    padding: Spacing.SCALE_24,
    backgroundColor: '#f8f9fa',
    minHeight: '100vh'
  };

  const headerStyle = {
    backgroundColor: 'white',
    padding: Spacing.SCALE_20,
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: Spacing.SCALE_24
  };

  const cardStyle = {
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: 'white'
  };

  const jsonContainerStyle = {
    backgroundColor: '#f8f9fa',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '8px',
    padding: Spacing.SCALE_16,
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: '14px',
    lineHeight: '1.5',
    maxHeight: '70vh',
    overflow: 'auto',
    whiteSpace: 'pre-wrap'
  };

  return (
    <div style={containerStyle}>
      <Container fluid>
        {/* Header */}
        <div style={headerStyle}>
          <Row className="align-items-center">
            <Col>
              <Title style={{ color: PRM_BLUE, margin: 0 }}>
                API Cache Data
              </Title>
              <Subtitle style={{ color: GRAY_DARK, margin: 0 }}>
                Cache ID: {urlCacheId || 'N/A'}
              </Subtitle>
            </Col>
            <Col xs="auto">
              <div style={{ display: 'flex', gap: Spacing.SCALE_12 }}>
                <Button 
                  variant="outline-primary" 
                  onClick={handleRefresh}
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? <CustomSpinner /> : 'Refresh'}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  onClick={handleClear}
                >
                  Clear
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Search Bar */}
        {status === 'succeeded' && cacheData && (
          <Card style={{ marginBottom: Spacing.SCALE_16, ...cardStyle }}>
            <Card.Body style={{ padding: Spacing.SCALE_16 }}>
              <Row className="align-items-center">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label style={{ fontSize: '14px', fontWeight: '600', color: GRAY_DARK }}>
                      Search in JSON Data
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search for flight numbers, keys, values..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      style={{
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                        fontSize: '14px'
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  {searchResults.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_8 }}>
                      <span style={{ fontSize: '14px', color: GRAY_DARK }}>
                        {currentResultIndex + 1} of {searchResults.length} results
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={scrollToPreviousResult}
                        disabled={searchResults.length === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={scrollToNextResult}
                        disabled={searchResults.length === 0}
                      >
                        ↓
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={clearSearch}
                      >
                        ✕
                      </Button>
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* Content */}
        {status === 'loading' && (
          <Card style={cardStyle}>
            <Card.Body style={{ textAlign: 'center', padding: Spacing.SCALE_32 }}>
              <CustomSpinner />
              <p style={{ marginTop: Spacing.SCALE_16, color: GRAY_DARK }}>
                Loading cache data...
              </p>
            </Card.Body>
          </Card>
        )}

        {status === 'failed' && (
          <Alert variant="danger">
            <Alert.Heading>Error Loading Cache Data</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" onClick={handleRefresh}>
              Try Again
            </Button>
          </Alert>
        )}

        {status === 'succeeded' && cacheData && (
          <Card style={cardStyle}>
            <Card.Header style={{ backgroundColor: PRM_BLUE, color: 'white' }}>
              <Title style={{ margin: 0, color: 'white', fontSize: '18px' }}>
                Cache Data
              </Title>
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              <div ref={jsonContainerRef} style={jsonContainerStyle}>
                {renderCacheData(cacheData)}
              </div>
            </Card.Body>
          </Card>
        )}

        {status === 'succeeded' && !cacheData && (
          <Card style={cardStyle}>
            <Card.Body style={{ textAlign: 'center', padding: Spacing.SCALE_32 }}>
              <p style={{ color: GRAY_DARK, margin: 0 }}>
                No cache data found for this ID.
              </p>
            </Card.Body>
          </Card>
        )}

        {!urlCacheId && (
          <Card style={cardStyle}>
            <Card.Body style={{ textAlign: 'center', padding: Spacing.SCALE_32 }}>
              <p style={{ color: GRAY_DARK, margin: 0 }}>
                No cache ID provided in URL.
              </p>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default ApiDataScreen;
